import React, { useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Grid,
    Divider,
    Alert,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Autocomplete,
} from '@mui/material';
import {
    Campaign as CampaignIcon,
    Add as AddIcon,
    Close as CloseIcon,
    FlashOn as FlashOnIcon,
    Map as MapIcon,
    Edit as EditIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import RychlyPoplachDialog from '../../components/RychlyPoplachDialog';
import MapPickerDialog from '../../components/MapPickerDialog';
import UserSelector from '../../components/UserSelector';
import { useAuth } from '../../context/AuthContext';
import {
    useCreateUdalostMutation,
    useTypyUdalostiQuery,
    usePodtypyByTypIdQuery,
    useAllUsersQuery,
    useSablonyQuery,
} from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { SablonaUdalosti } from '../../api/JsdhNotifikaceService/generated';
import PotvrzeniVyhlaseniDialog from '../../components/PotvrzeniVyhlaseniDialog';
import { useActiveTechnikaQuery } from '../../api/JsdhNotifikaceService/service/TechnikaMutations';
import NedostupnaTechnikaWarning from '../../components/NedostupnaTechnikaWarning';
import type { Technika } from '../../api/JsdhNotifikaceService/generated/models/Technika';
import type { CreateUdalostRequest } from '../../api/JsdhNotifikaceService/generated';
import type { LokaceSearchResult } from '../../api/JsdhNotifikaceService/generated/models/LokaceSearchResult';
import JsdhNotifikaceService from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';

const VyhlaseniPoplachPage: React.FC = () => {
    const { isAdmin } = useAuth();
    const createMutation = useCreateUdalostMutation();
    const [mode, setMode] = useState<'select' | 'manual' | 'sablony'>('select');
    const [rychlyDialogOpen, setRychlyDialogOpen] = useState(false);
    const [rychlyDialogSablona, setRychlyDialogSablona] = useState<SablonaUdalosti | undefined>(undefined);
    const [mapPickerOpen, setMapPickerOpen] = useState(false);
    const [potvrzeniSablona, setPotvrzeniSablona] = useState<SablonaUdalosti | null>(null);

    // Queries for types and subtypes
    const { data: typy, isLoading: isLoadingTypy } = useTypyUdalostiQuery();
    const [selectedTypId, setSelectedTypId] = useState<number | undefined>(undefined);
    const { data: podtypy, isLoading: isLoadingPodtypy } = usePodtypyByTypIdQuery(selectedTypId);

    // Users query for recipient selection
    const { data: allUsers = [], isLoading: isLoadingUsers } = useAllUsersQuery();
    const { data: sablony = [] } = useSablonyQuery();

    const [selectedUserIds, setSelectedUserIds] = useState<number[] | null>(null); // null = všem

    const [formData, setFormData] = useState<CreateUdalostRequest>({
        popis: '',
        obec: '',
        udalost: '',
        gps: '',
        kraj: '',
        cast: '',
        ulice: '',
        cisloPopisne: '',
        objekt: '',
        upresneni: '',
        technikaJednotky: [],
        dalsiTechnikaJednotek: [],
        odeslatNotifikace: true,
        generovatTts: true,
    });

    const [technikaInput, setTechnikaInput] = useState('');
    const [dalsiTechnikaInput, setDalsiTechnikaInput] = useState('');
    const [selectedTechnikaIds, setSelectedTechnikaIds] = useState<number[]>([]);
    const { data: aktivniTechnika = [] } = useActiveTechnikaQuery();
    const [geocodedObecGps, setGeocodedObecGps] = useState<[number, number] | null>(null);

    // Autocomplete pro adresy
    const [adresaInput, setAdresaInput] = useState('');
    const [lokaceOptions, setLokaceOptions] = useState<LokaceSearchResult[]>([]);
    const [isLoadingLokace, setIsLoadingLokace] = useState(false);
    const lokaceSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleAdresaInputChange = (value: string) => {
        setAdresaInput(value);

        if (lokaceSearchTimeoutRef.current) {
            clearTimeout(lokaceSearchTimeoutRef.current);
        }

        if (value.length >= 2) {
            lokaceSearchTimeoutRef.current = setTimeout(async () => {
                setIsLoadingLokace(true);
                try {
                    const results = await JsdhNotifikaceService.searchLokace(value);
                    setLokaceOptions(results);
                } catch {
                    setLokaceOptions([]);
                }
                setIsLoadingLokace(false);
            }, 300);
        } else {
            setLokaceOptions([]);
        }
    };

    // Při výběru z nabídky - vyplnit více polí
    const handleLokaceSelect = (value: string | LokaceSearchResult | null) => {
        if (!value) return;

        if (typeof value === 'string') {
            // Ruční zadání - nic nevyplňovat
            setAdresaInput(value);
        } else {
            // Výběr z nabídky - vyplnit pole
            setAdresaInput(value.displayName);
            setFormData({
                ...formData,
                obec: value.obec || value.nazev,
                kraj: value.kraj || formData.kraj,
                cast: value.castObce || formData.cast,
                // HO-161: vyhledaná adresa ulici nese, dřív se zahazovala.
                ulice: value.ulice || '',
                cisloPopisne: value.cisloPopisne || formData.cisloPopisne,
                gps: `${value.lat},${value.lng}`,
            });
            // Aktualizovat geocoded GPS pro mapu
            setGeocodedObecGps([value.lat, value.lng]);
        }
    };

    const [lastResponse, setLastResponse] = useState<{
        pocetOdeslanychNotifikaci: number;
        ttsVytvoreno: boolean;
        message: string;
    } | null>(null);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.popis || !formData.obec || !formData.typId || !formData.podtypId) return;

        // Připravit data s userIds a technikaIds
        const requestData: CreateUdalostRequest = {
            ...formData,
            userIds: formData.odeslatNotifikace ? selectedUserIds : undefined,
            technikaIds: selectedTechnikaIds.length > 0 ? selectedTechnikaIds : undefined,
        };

        createMutation.mutate(requestData, {
            onSuccess: (response) => {
                setLastResponse({
                    pocetOdeslanychNotifikaci: response.pocetOdeslanychNotifikaci,
                    ttsVytvoreno: response.ttsVytvoreno,
                    message: response.message,
                });
                // Reset form
                setSelectedTypId(undefined);
                setSelectedUserIds(null);
                setSelectedTechnikaIds([]);
                setAdresaInput('');
                setGeocodedObecGps(null);
                setFormData({
                    popis: '',
                    obec: '',
                    udalost: '',
                    gps: '',
                    kraj: '',
                    cast: '',
                    ulice: '',
                    cisloPopisne: '',
                    objekt: '',
                    upresneni: '',
                    technikaJednotky: [],
                    dalsiTechnikaJednotek: [],
                    odeslatNotifikace: true,
                    generovatTts: true,
                });
            },
        });
    };

    const addTechnikaFromAutocomplete = (value: string | Technika | null) => {
        if (!value) return;

        if (typeof value === 'string') {
            // Ruční vstup textu
            if (value.trim()) {
                setFormData({
                    ...formData,
                    technikaJednotky: [...(formData.technikaJednotky || []), value.trim()],
                });
            }
        } else {
            // Výběr z DB - zamezit duplicitám
            if (selectedTechnikaIds.includes(value.id)) return;
            const text = `${value.nazev} - ${value.volaciZnak}`;
            setFormData({
                ...formData,
                technikaJednotky: [...(formData.technikaJednotky || []), text],
            });
            setSelectedTechnikaIds([...selectedTechnikaIds, value.id]);
        }
        setTechnikaInput('');
    };

    const addTechnika = () => {
        if (technikaInput.trim()) {
            setFormData({
                ...formData,
                technikaJednotky: [...(formData.technikaJednotky || []), technikaInput.trim()],
            });
            setTechnikaInput('');
        }
    };

    const removeTechnika = (index: number) => {
        const removedText = formData.technikaJednotky?.[index];
        setFormData({
            ...formData,
            technikaJednotky: formData.technikaJednotky?.filter((_, i) => i !== index),
        });
        // Odebrat odpovídající ID z DB výběru
        if (removedText) {
            const matchedTechnika = aktivniTechnika.find(
                t => `${t.nazev} - ${t.volaciZnak}` === removedText
            );
            if (matchedTechnika) {
                setSelectedTechnikaIds(prev => prev.filter(id => id !== matchedTechnika.id));
            }
        }
    };

    const addDalsiTechnika = () => {
        if (dalsiTechnikaInput.trim()) {
            setFormData({
                ...formData,
                dalsiTechnikaJednotek: [...(formData.dalsiTechnikaJednotek || []), dalsiTechnikaInput.trim()],
            });
            setDalsiTechnikaInput('');
        }
    };

    const removeDalsiTechnika = (index: number) => {
        setFormData({
            ...formData,
            dalsiTechnikaJednotek: formData.dalsiTechnikaJednotek?.filter((_, i) => i !== index),
        });
    };

    const handleGpsSelect = (lat: number, lng: number) => {
        setFormData({ ...formData, gps: `${lat},${lng}` });
    };

    const handleSablonaClickFromList = (sablona: SablonaUdalosti) => {
        if (sablona.rychleVyhlaseni) {
            setPotvrzeniSablona(sablona);
        } else {
            setRychlyDialogSablona(sablona);
            setRychlyDialogOpen(true);
        }
    };

    const handleRychlyDialogClose = () => {
        setRychlyDialogOpen(false);
        setRychlyDialogSablona(undefined);
    };

    // Non-admin view
    if (!isAdmin) {
        return (
            <Box>
                <PageHeader title="Vyhlášení poplachu" />
                <Alert severity="info" sx={{ mb: 2 }}>
                    Pro vyhlášení poplachu jsou potřeba administrátorská oprávnění.
                </Alert>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                        Tato stránka slouží k vyhlášení nového poplachu pro členy jednotky.
                        Pouze administrátoři mohou vyhlašovat poplachy.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // === REŽIM VÝBĚRU ===
    if (mode === 'select') {
        return (
            <Box>
                <PageHeader title="Vyhlášení poplachu" />

                {lastResponse && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setLastResponse(null)}>
                        {lastResponse.message}
                        <br />
                        Odesláno notifikací: {lastResponse.pocetOdeslanychNotifikaci}
                        {lastResponse.ttsVytvoreno && ' | TTS vygenerováno'}
                    </Alert>
                )}

                <Box display="flex" flexDirection="column" gap={2} maxWidth={500} mx="auto" mt={4}>
                    <Paper
                        sx={{
                            p: 4,
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        }}
                        onClick={() => setMode('manual')}
                    >
                        <EditIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h5" gutterBottom>
                            Ručně zadat poplach
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Vyplníte formulář a vyhlásíte poplach ručně
                        </Typography>
                    </Paper>

                    <Paper
                        sx={{
                            p: 4,
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        }}
                        onClick={() => setMode('sablony')}
                    >
                        <FlashOnIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                        <Typography variant="h5" gutterBottom>
                            Šablony poplachu
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Vyberte z přednastavených šablon pro rychlé vyhlášení
                        </Typography>
                    </Paper>
                </Box>

                <PotvrzeniVyhlaseniDialog
                    open={!!potvrzeniSablona}
                    onClose={() => setPotvrzeniSablona(null)}
                    sablona={potvrzeniSablona}
                />
            </Box>
        );
    }

    // === REŽIM ŠABLON ===
    if (mode === 'sablony') {
        return (
            <Box>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setMode('select')}
                    sx={{ mb: 2 }}
                >
                    Zpět
                </Button>

                <PageHeader title="Šablony poplachu" />

                {sablony.length === 0 ? (
                    <Alert severity="info">
                        Zatím nejsou vytvořeny žádné šablony.
                    </Alert>
                ) : (
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {sablony.map((s) => (
                            <Paper
                                key={s.id}
                                sx={{
                                    p: 3,
                                    cursor: 'pointer',
                                    minWidth: 200,
                                    maxWidth: 300,
                                    flex: '1 1 200px',
                                    textAlign: 'center',
                                    bgcolor: s.barva || undefined,
                                    color: s.barva ? '#fff' : undefined,
                                    transition: 'all 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 4, opacity: 0.9 },
                                }}
                                onClick={() => handleSablonaClickFromList(s)}
                            >
                                {s.rychleVyhlaseni && (
                                    <FlashOnIcon sx={{ fontSize: 32, mb: 0.5, color: 'inherit' }} />
                                )}
                                <Typography variant="h6" sx={{ color: 'inherit' }}>
                                    {s.nazev}
                                </Typography>
                                {s.popis && (
                                    <Typography variant="body2" sx={{ mt: 1, color: 'inherit', opacity: 0.85 }}>
                                        {s.popis}
                                    </Typography>
                                )}
                                {s.rychleVyhlaseni && (
                                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'inherit', opacity: 0.7 }}>
                                        Rychlé vyhlášení
                                    </Typography>
                                )}
                            </Paper>
                        ))}
                    </Box>
                )}

                <RychlyPoplachDialog
                    open={rychlyDialogOpen}
                    onClose={handleRychlyDialogClose}
                    initialSablona={rychlyDialogSablona}
                />

                <PotvrzeniVyhlaseniDialog
                    open={!!potvrzeniSablona}
                    onClose={() => setPotvrzeniSablona(null)}
                    sablona={potvrzeniSablona}
                />
            </Box>
        );
    }

    // === REŽIM MANUÁLNÍ ===
    return (
        <Box>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setMode('select')}
                sx={{ mb: 2 }}
            >
                Zpět
            </Button>

            <PageHeader title="Ručně zadat poplach" />

            <MapPickerDialog
                open={mapPickerOpen}
                onClose={() => setMapPickerOpen(false)}
                onSelect={handleGpsSelect}
                initialGps={formData.gps}
                geocodedObecGps={geocodedObecGps}
            />

            {lastResponse && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setLastResponse(null)}>
                    {lastResponse.message}
                    <br />
                    Odesláno notifikací: {lastResponse.pocetOdeslanychNotifikaci}
                    {lastResponse.ttsVytvoreno && ' | TTS vygenerováno'}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Povinné údaje */}
                        <Grid size={12}>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Základní údaje (povinné)
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Popis události"
                                fullWidth
                                required
                                multiline
                                rows={2}
                                value={formData.popis}
                                onChange={(e) => setFormData({ ...formData, popis: e.target.value })}
                                placeholder="např. Požár rodinného domu"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                freeSolo
                                options={lokaceOptions}
                                loading={isLoadingLokace}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.nazev}
                                filterOptions={(x) => x}
                                inputValue={adresaInput}
                                onInputChange={(_, value) => handleAdresaInputChange(value)}
                                onChange={(_, value) => handleLokaceSelect(value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Vyhledat adresu"
                                        fullWidth
                                        placeholder="Zadejte obec, ulici nebo adresu..."
                                        helperText="Vyberte z nabídky pro automatické vyplnění polí"
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const { key, ...rest } = props;
                                    return (
                                        <li key={option.id} {...rest}>
                                            <Box>
                                                <Typography>{option.nazev}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.displayName}
                                                </Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth required error={!selectedTypId}>
                                <InputLabel>Typ události *</InputLabel>
                                <Select
                                    value={selectedTypId || ''}
                                    label="Typ události *"
                                    onChange={(e) => {
                                        const typId = e.target.value as number;
                                        setSelectedTypId(typId || undefined);
                                        setFormData({ ...formData, typId: typId || undefined, podtypId: undefined });
                                    }}
                                    endAdornment={isLoadingTypy ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
                                >
                                    {typy?.map((typ) => (
                                        <MenuItem key={typ.id} value={typ.id}>
                                            {typ.nazev}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth required disabled={!selectedTypId} error={!!selectedTypId && !formData.podtypId}>
                                <InputLabel>Podtyp události *</InputLabel>
                                <Select
                                    value={formData.podtypId || ''}
                                    label="Podtyp události *"
                                    onChange={(e) => {
                                        const podtypId = e.target.value as number;
                                        setFormData({ ...formData, podtypId: podtypId || undefined });
                                    }}
                                    endAdornment={isLoadingPodtypy ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
                                >
                                    {podtypy?.map((podtyp) => (
                                        <MenuItem key={podtyp.id} value={podtyp.id}>
                                            {podtyp.nazev}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                Volitelné údaje
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Typ události (text)"
                                fullWidth
                                value={formData.udalost || ''}
                                onChange={(e) => setFormData({ ...formData, udalost: e.target.value })}
                                placeholder="Alternativně zadat textově"
                                helperText="Použije se pokud chcete doplnit popis"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Obec"
                                fullWidth
                                required
                                value={formData.obec || ''}
                                onChange={(e) => setFormData({ ...formData, obec: e.target.value })}
                                placeholder="např. Praha"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Část obce"
                                fullWidth
                                value={formData.cast || ''}
                                onChange={(e) => setFormData({ ...formData, cast: e.target.value })}
                                placeholder="např. Staré Město"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Ulice"
                                fullWidth
                                value={formData.ulice || ''}
                                onChange={(e) => setFormData({ ...formData, ulice: e.target.value })}
                                placeholder="např. Palackého"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Kraj"
                                fullWidth
                                value={formData.kraj || ''}
                                onChange={(e) => setFormData({ ...formData, kraj: e.target.value })}
                                placeholder="např. Středočeský"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Číslo popisné"
                                fullWidth
                                value={formData.cisloPopisne || ''}
                                onChange={(e) => setFormData({ ...formData, cisloPopisne: e.target.value })}
                                placeholder="např. 123"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Objekt"
                                fullWidth
                                value={formData.objekt || ''}
                                onChange={(e) => setFormData({ ...formData, objekt: e.target.value })}
                                placeholder="např. Základní škola"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box display="flex" gap={1}>
                                <TextField
                                    label="GPS souřadnice"
                                    fullWidth
                                    value={formData.gps || ''}
                                    onChange={(e) => setFormData({ ...formData, gps: e.target.value })}
                                    placeholder="např. 50.0755,14.4378"
                                />
                                <Button
                                    variant="outlined"
                                    onClick={() => setMapPickerOpen(true)}
                                    sx={{ minWidth: 'auto', px: 2 }}
                                >
                                    <MapIcon />
                                </Button>
                            </Box>
                        </Grid>

                        <Grid size={12}>
                            <TextField
                                label="Upřesnění"
                                fullWidth
                                multiline
                                rows={2}
                                value={formData.upresneni || ''}
                                onChange={(e) => setFormData({ ...formData, upresneni: e.target.value })}
                                placeholder="např. u hlavní silnice, za kostelem"
                            />
                        </Grid>

                        {/* Technika jednotky */}
                        <Grid size={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                Technika
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Technika jednotky
                            </Typography>
                            <Box display="flex" gap={1} mb={1}>
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    size="small"
                                    options={aktivniTechnika.filter(t => !selectedTechnikaIds.includes(t.id))}
                                    getOptionLabel={(option) =>
                                        typeof option === 'string'
                                            ? option
                                            : `${option.nazev} - ${option.volaciZnak}${option.spz ? ` (${option.spz})` : ''}`
                                    }
                                    inputValue={technikaInput}
                                    onInputChange={(_, newValue) => setTechnikaInput(newValue)}
                                    onChange={(_, value) => {
                                        addTechnikaFromAutocomplete(value);
                                    }}
                                    value={null}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Vyberte z číselníku nebo zadejte vlastní"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addTechnika();
                                                }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <Box>
                                                <Typography variant="body2">
                                                    {option.nazev} - <strong>{option.volaciZnak}</strong>
                                                </Typography>
                                                {option.spz && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        SPZ: {option.spz}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    clearOnBlur={false}
                                    selectOnFocus
                                    handleHomeEndKeys
                                />
                                <IconButton onClick={addTechnika} color="primary">
                                    <AddIcon />
                                </IconButton>
                            </Box>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {formData.technikaJednotky?.map((t, i) => (
                                    <Chip
                                        key={i}
                                        label={t}
                                        onDelete={() => removeTechnika(i)}
                                        deleteIcon={<CloseIcon />}
                                        size="small"
                                    />
                                ))}
                            </Box>
                            <NedostupnaTechnikaWarning
                                aktivniTechnika={aktivniTechnika}
                                selectedTechnikaIds={selectedTechnikaIds}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Další technika jednotek
                            </Typography>
                            <Box display="flex" gap={1} mb={1}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={dalsiTechnikaInput}
                                    onChange={(e) => setDalsiTechnikaInput(e.target.value)}
                                    placeholder="např. HZS - CAS 30"
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDalsiTechnika())}
                                />
                                <IconButton onClick={addDalsiTechnika} color="primary">
                                    <AddIcon />
                                </IconButton>
                            </Box>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {formData.dalsiTechnikaJednotek?.map((t, i) => (
                                    <Chip
                                        key={i}
                                        label={t}
                                        onDelete={() => removeDalsiTechnika(i)}
                                        deleteIcon={<CloseIcon />}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Grid>

                        {/* Nastavení */}
                        <Grid size={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                                Nastavení
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.odeslatNotifikace ?? true}
                                        onChange={(e) =>
                                            setFormData({ ...formData, odeslatNotifikace: e.target.checked })
                                        }
                                    />
                                }
                                label="Odeslat notifikace členům"
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.generovatTts ?? true}
                                        onChange={(e) =>
                                            setFormData({ ...formData, generovatTts: e.target.checked })
                                        }
                                    />
                                }
                                label="Generovat TTS audio"
                            />
                        </Grid>

                        {formData.odeslatNotifikace && (
                            <Grid size={12}>
                                <UserSelector
                                    users={allUsers}
                                    isLoading={isLoadingUsers}
                                    selectedUserIds={selectedUserIds}
                                    onSelectionChange={setSelectedUserIds}
                                    disabled={createMutation.isPending}
                                />
                            </Grid>
                        )}

                        {/* Submit */}
                        <Grid size={12}>
                            <Divider sx={{ my: 2 }} />
                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="error"
                                    size="large"
                                    startIcon={<CampaignIcon />}
                                    disabled={
                                        !formData.popis ||
                                        !formData.obec ||
                                        !formData.typId ||
                                        !formData.podtypId ||
                                        createMutation.isPending ||
                                        (formData.odeslatNotifikace && selectedUserIds !== null && selectedUserIds.length === 0)
                                    }
                                    sx={{ minWidth: 200 }}
                                >
                                    {createMutation.isPending ? 'Vyhlašuji...' : 'VYHLÁSIT POPLACH'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default VyhlaseniPoplachPage;
